<template>
  <VContainer>
    <AppPreloader v-if="!fetched || company.isFetching" />
    <VAlert v-else-if="company.fetchError" type="error">
      {{ company.fetchError }}
    </VAlert>
    <div v-else-if="company.isFetched">
      <VRow justify="center">
        <VCol cols="12" md="3" lg="3" xl="2">
          <CompanySmallCard :company="company.data" :show-tabs="true" />
        </VCol>
        <VCol cols="12" md="8">
          <CompanyCard :company="company.data" />
          <VCard elevation="1" class="pa-3 pa-md-7 px-md-12 mb-5">
            <h3
              class="d-flex justify-space-between align-end"
              :class="$vuetify.breakpoint.mdAndUp ? 'display-2' : 'title'"
            >
              {{ $t('vacancies') }}
              <span class="body-2 grey--text text--lighten-1 mb-1">
                <template v-if="vacancies.isFetched">
                  {{ $tc('vacancy', vacancies.items.length) }}
                </template>
                <span v-else />
              </span>
            </h3>
            <VDivider class="mt-3" />
            <CompanyVacanciesContainer :company-id="id" :limit="vacanciesLimit" />
            <div v-show="(vacancies.items.length > vacanciesLimit)">
              <VDivider class="mb-3 mb-md-5" />
              <RouterLink
                :to="{ name: 'vacancies', params: { companyId: id }}"
                class="subtitle-2 text-decoration-none"
              >
                {{ $t('more') }} {{ $tc('vacancy', vacancies.items.length - vacanciesLimit) }}
              </RouterLink>
            </div>
          </VCard>
        </VCol>
      </VRow>
    </div>
    <div v-else />
  </VContainer>
</template>

<script>
import { mdiChevronRight } from '@mdi/js';
import { mapState } from 'vuex';
import { safeGet } from '@/functions';
import AppPreloader from '../components/AppPreloader.vue';
import CompanySmallCard from '../components/Company/CompanySmallCard.vue';
import CompanyCard from '../components/Company/CompanyCard.vue';
import CompanyVacanciesContainer from '../components/Company/CompanyVacanciesContainer.vue';

export default {
  name: 'Company',
  components: {
    CompanyVacanciesContainer,
    CompanySmallCard,
    CompanyCard,
    AppPreloader,
  },
  metaInfo() {
    return {
      title: this.pageTitle,
    };
  },
  props: {
    id: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      chevron: mdiChevronRight,
      vacanciesLimit: 5,
      fetched: false,
    };
  },
  computed: {
    pageTitle() {
      const companyName = safeGet(this.company, ['data', 'name']);
      if (!companyName) {
        return null;
      }
      return `${this.$t('companyVacancies')} ${companyName}. ${this.$t('companyInfo')}.`;
    },
    ...mapState('company', ['company', 'vacancies']),
  },
  async serverPrefetch() {
    await this.fetch();
    if (this.company.fetchError) {
      this.fetched = true;
      throw new Error(String(this.company.fetchCode));
    }
  },
  async beforeMount() {
    if (!this.company.isFetched) {
      await this.fetch();
    } else {
      this.fetched = true;
    }
  },
  methods: {
    async fetch() {
      await Promise.all([
        this.$store.dispatch('company/fetchVacancies', this.id),
        this.$store.dispatch('company/fetchCompany', this.id),
      ]);

      if (this.company?.data?.companyTemplateSettings && this.$route?.query?.preview !== 'true') {
        window.location.href = `${process.env.VUE_APP_CAREER_PORTAL_URL}/${this.company.data.id}`;
      } else {
        this.fetched = true;
      }
    },
  },
};
</script>

<i18n locale="en" lang="yaml">
  vacancy: 0 jobs | {n} job | {n} jobs | {n} jobs
  vacancies: jobs
  more: More
  companyVacancies: Company jobs
  companyInfo: Company information
</i18n>

<i18n locale="ru" lang="yaml">
  vacancy: 0 вакансий | {n} вакансия | {n} вакансии | {n} вакансий
  vacancies: Вакансии
  more: Ещё
  companyVacancies: Вакансии компании
  companyInfo: Информация о компании
</i18n>
